/**
 * React Node View für den ViewSnapshot-Node.
 *
 * Lese-Modus: Kompakte Inline-Referenz (Kamera-Icon + Label + Zeitstempel).
 * Edit-Modus: Block-Karte mit Thumbnail, Drag-Handle, Kamera-Info, Edit/Delete.
 */

import React, { useMemo, useCallback, useState } from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { NodeViewProps } from '@tiptap/core'
import { ViewSnapshot } from '../../types/annotations'
import { C } from '../../styles/theme'
import { EMBED_DRAG_DATA } from './EmbedDragPlugin'

const SNAP_COLOR = '#6366f1'

export function ViewSnapshotView({ node, extension, updateAttributes, editor, getPos }: NodeViewProps) {
  const {
    snapshot: snapshotJson,
    label,
    returnOnLeave,
  } = node.attrs as { snapshot: string; label: string; returnOnLeave: boolean }

  const [isHovered, setIsHovered] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editLabel, setEditLabel] = useState(label)
  const isEditable = editor?.isEditable ?? false

  const snap = useMemo<ViewSnapshot | null>(() => {
    try { return JSON.parse(snapshotJson) }
    catch { return null }
  }, [snapshotJson])

  function handleClick() {
    if (isEditing) return
    const cb = extension.options.onSnapshotClick
    if (cb) cb(snapshotJson)
  }

  const handleReturnToggle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    updateAttributes({ returnOnLeave: e.target.checked })
  }, [updateAttributes])

  function handleDragStart(e: React.DragEvent) {
    const pos = getPos()
    if (typeof pos !== 'number') return
    e.dataTransfer.setData(EMBED_DRAG_DATA, JSON.stringify({ pos }))
    e.dataTransfer.effectAllowed = 'move'
    const card = (e.target as HTMLElement).closest('.embed-card')
    if (card) e.dataTransfer.setDragImage(card as HTMLElement, 20, 20)
  }

  const handleDelete = useCallback(() => {
    const pos = getPos()
    if (typeof pos !== 'number') return
    editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run()
  }, [editor, getPos, node.nodeSize])

  const handleEditSave = useCallback(() => {
    const updatedSnapshot = snap ? { ...snap, label: editLabel } : null
    updateAttributes({
      label: editLabel,
      ...(updatedSnapshot ? { snapshot: JSON.stringify(updatedSnapshot) } : {}),
    })
    setIsEditing(false)
  }, [editLabel, snap, updateAttributes])

  const hasClip = !!snap?.clipBox
  const camInfo = snap
    ? `${fmt(snap.camera.position.x)}, ${fmt(snap.camera.position.y)}, ${fmt(snap.camera.position.z)}`
    : null
  const targetInfo = snap
    ? `${fmt(snap.camera.target.x)}, ${fmt(snap.camera.target.y)}, ${fmt(snap.camera.target.z)}`
    : null

  const timeStr = snap?.capturedAt
    ? new Date(snap.capturedAt).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : null

  // ── Lese-Modus: kompakte Inline-Referenz ──────────────────────────────────
  if (!isEditable) {
    return (
      <NodeViewWrapper>
        <span
          data-type="view-snapshot"
          data-label={label}
          data-snapshot={snapshotJson}
          className="embed-inline"
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            color: SNAP_COLOR,
            background: isHovered ? `${SNAP_COLOR}12` : 'transparent',
            borderBottomColor: `${SNAP_COLOR}44`,
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke={SNAP_COLOR} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12, flexShrink: 0 }}>
            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
            <circle cx="12" cy="13" r="3"/>
          </svg>
          <span className="embed-inline__label">{label}</span>
          {timeStr && (
            <span className="embed-inline__dim">{timeStr}</span>
          )}
        </span>
      </NodeViewWrapper>
    )
  }

  // ── Edit-Modus: volle Block-Karte ─────────────────────────────────────────
  return (
    <NodeViewWrapper>
      <div
        data-type="view-snapshot"
        data-label={label}
        data-snapshot={snapshotJson}
        className="embed-card"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          background: isHovered ? `${SNAP_COLOR}0d` : `${SNAP_COLOR}08`,
          borderTopColor: isHovered ? `${SNAP_COLOR}55` : `${SNAP_COLOR}33`,
          borderRightColor: isHovered ? `${SNAP_COLOR}55` : `${SNAP_COLOR}33`,
          borderBottomColor: isHovered ? `${SNAP_COLOR}55` : `${SNAP_COLOR}33`,
          borderLeftColor: SNAP_COLOR,
          borderLeftWidth: '3px',
        }}
      >
        <div className="embed-card__grip" draggable="true" onDragStart={handleDragStart}>
          <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" opacity="0.35">
            <circle cx="3" cy="2" r="1.2"/><circle cx="7" cy="2" r="1.2"/>
            <circle cx="3" cy="6" r="1.2"/><circle cx="7" cy="6" r="1.2"/>
            <circle cx="3" cy="10" r="1.2"/><circle cx="7" cy="10" r="1.2"/>
            <circle cx="3" cy="14" r="1.2"/><circle cx="7" cy="14" r="1.2"/>
          </svg>
        </div>

        {snap?.thumbnail && (
          <div className="embed-card__thumb" onClick={handleClick}>
            <img
              src={snap.thumbnail}
              alt={label}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}

        <div className="embed-card__body" onClick={handleClick} style={{ cursor: 'pointer' }}>
          <div className="embed-card__header">
            <svg viewBox="0 0 24 24" fill="none" stroke={SNAP_COLOR} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0 }}>
              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
              <circle cx="12" cy="13" r="3"/>
            </svg>
            {isEditing ? (
              <input
                className="embed-card__edit-input"
                value={editLabel}
                onChange={e => setEditLabel(e.target.value)}
                onBlur={handleEditSave}
                onKeyDown={e => e.key === 'Enter' && handleEditSave()}
                autoFocus
                onClick={e => e.stopPropagation()}
              />
            ) : (
              <span className="embed-card__title" style={{ color: SNAP_COLOR }}>{label}</span>
            )}
            {isEditable && (
              <button
                className="embed-card__action-btn embed-card__action-btn--inline"
                title="Bearbeiten"
                onClick={e => { e.stopPropagation(); setEditLabel(label); setIsEditing(!isEditing) }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            )}
            <span className="embed-card__badge" style={{ background: `${SNAP_COLOR}20`, color: SNAP_COLOR }}>Kamera</span>
            {hasClip && (
              <span className="embed-card__badge" style={{ background: `${SNAP_COLOR}20`, color: SNAP_COLOR }}>ClipBox</span>
            )}
          </div>

          <div className="embed-card__meta">
            {camInfo && (
              <span className="embed-card__meta-item">
                <span className="embed-card__meta-label">Position</span>
                <span style={{ fontFamily: C.fontMono }}>{camInfo}</span>
              </span>
            )}
            {targetInfo && (
              <span className="embed-card__meta-item">
                <span className="embed-card__meta-label">Target</span>
                <span style={{ fontFamily: C.fontMono }}>{targetInfo}</span>
              </span>
            )}
            {snap?.capturedAt && (
              <span className="embed-card__meta-item embed-card__meta-dim">
                {new Date(snap.capturedAt).toLocaleString('de-DE', {
                  day: '2-digit', month: '2-digit', year: '2-digit',
                  hour: '2-digit', minute: '2-digit',
                })}
              </span>
            )}
          </div>
        </div>

        <div className="embed-card__actions" onClick={e => e.stopPropagation()}>
          <div className="embed-card__tooltip-wrap">
            <label className={`embed-card__checkbox-label${returnOnLeave ? ' is-active' : ''}`}>
              <input
                type="checkbox"
                checked={!!returnOnLeave}
                onChange={handleReturnToggle}
              />
              <span className="embed-card__checkbox-text">Kamera zurücksetzen</span>
            </label>
            <div className="embed-card__tooltip">
              Automatisches Zurücksetzen der Kamera nach Verlassen der Ansicht
            </div>
          </div>
          {isEditable && (
            <button className="embed-card__action-btn embed-card__action-btn--danger" title="Entfernen" onClick={handleDelete}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  )
}

function fmt(n: number): string {
  return n.toFixed(1)
}
